import './c100-fonts'
import { Box, Container, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import PageProps from '../_types/page-props.interface'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { useError } from '../context/error'
import LoadingTable from '../components/loading/table.loading'
import { Carnivore100Service } from '../services/carnivore100.service'
import { C100HistorySummary, C100HistoryVersion } from '../_types/carnivore100.type'
import { LIST_NAME, LIST_PATH } from '../components/carnivore100/list-name'
import { c100Tokens, C100Tokens } from '../components/carnivore100/c100-tokens'
import VersionRail from '../components/carnivore100/history/version-rail'
import ChangesPanel from '../components/carnivore100/history/changes-panel'
import FrozenTable from '../components/carnivore100/history/frozen-table'
import TickerTimelinePanel from '../components/carnivore100/history/ticker-timeline-panel'
import { fmtPublishDate, parseVersionParam } from './carnivore100/carnivore100.history.util'

const MONO = "'IBM Plex Mono', monospace"

// The archive of published lists. Everything here is frozen at publish — no
// quotes, no timeframes — so a past list reads exactly as subscribers got it.
// The selected list (?v=) and open ticker (?ticker=) live in the URL so a
// specific week or name can be linked to.
export default function Carnivore33HistoryPage(props: PageProps) {
  useDocumentTitle(props.title)
  const { setError } = useError()
  const service = useMemo(() => new Carnivore100Service(setError), [setError])
  const theme = useTheme()
  const t = c100Tokens(theme)
  const [params, setParams] = useSearchParams()

  const [versions, setVersions] = useState<C100HistorySummary[] | null>(null)
  const [listErrored, setListErrored] = useState(false)
  const [detail, setDetail] = useState<C100HistoryVersion | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailErrored, setDetailErrored] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const selected = parseVersionParam(params.get('v')) ?? versions?.[0]?.version ?? null
  const openTicker = params.get('ticker')

  const updateParams = (next: { v?: number; ticker?: string | null }) => {
    setParams(prev => {
      const p = new URLSearchParams(prev)
      if (next.v !== undefined) p.set('v', String(next.v))
      if (next.ticker === null) p.delete('ticker')
      else if (next.ticker !== undefined) p.set('ticker', next.ticker)
      return p
    })
  }

  useEffect(() => {
    let active = true
    ;(async () => {
      setListErrored(false)
      const res = await service.getHistory()
      if (!active) return
      if (Array.isArray(res)) setVersions(res)
      else setListErrored(true)
    })()
    return () => {
      active = false
    }
  }, [service, reloadKey])

  useEffect(() => {
    if (selected === null) return
    let active = true
    ;(async () => {
      setDetailLoading(true)
      setDetailErrored(false)
      const res = await service.getHistoryVersion(selected)
      if (!active) return
      if (res && !(res as any).errMsg) setDetail(res as C100HistoryVersion)
      else setDetailErrored(true)
      setDetailLoading(false)
    })()
    return () => {
      active = false
    }
  }, [service, selected, reloadKey])

  const retry = () => setReloadKey(k => k + 1)

  return (
    <Box sx={{ bgcolor: t.page, minHeight: '100vh' }}>
      <Container sx={{ display: 'flex', flexDirection: 'column', pb: 4, pt: 3 }}>
        <Box sx={{ mb: 2.5 }}>
          <Box
            component={RouterLink}
            to={LIST_PATH}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: '0.06em',
              color: t.muted,
              textDecoration: 'none',
              mb: 1,
              '&:hover': { color: t.accent }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} /> Back to the live list
          </Box>
          <Typography
            component="h1"
            sx={{
              fontFamily: MONO,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: t.text,
              textTransform: 'uppercase',
              lineHeight: 1.1
            }}
          >
            {LIST_NAME} History
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: '0.12em',
              color: t.muted,
              textTransform: 'uppercase',
              mt: 0.5
            }}
          >
            Every published list, exactly as it went out
          </Typography>
        </Box>

        {listErrored && (
          <Notice t={t} onRetry={retry}>
            Couldn't load the published lists.
          </Notice>
        )}

        {!listErrored && versions === null && <LoadingTable rowCount={8} columnCount={8} />}

        {versions !== null && versions.length === 0 && (
          <Notice t={t}>No lists have been published yet. Check back soon.</Notice>
        )}

        {versions !== null && versions.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
            <VersionRail
              versions={versions}
              selected={selected}
              onSelect={v => updateParams({ v, ticker: null })}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {detailLoading && <LoadingTable rowCount={8} columnCount={8} />}

              {!detailLoading && detailErrored && (
                <Notice t={t} onRetry={retry}>
                  {selected !== null && !versions.some(v => v.version === selected)
                    ? "That list doesn't exist."
                    : "Couldn't load this list."}
                </Notice>
              )}

              {!detailLoading && !detailErrored && detail && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1.5
                    }}
                  >
                    <Box>
                      <Typography
                        component="h2"
                        sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color: t.text }}
                      >
                        {fmtPublishDate(detail.publishedAt)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: MONO,
                          fontSize: 10,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: detail.isCurrent ? t.accent : t.muted
                        }}
                      >
                        {detail.isCurrent ? 'Current list · as published' : 'As published'}
                        {` · ${detail.entries.length} names`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <StepButton
                        t={t}
                        label={detail.previous ? `Previous: ${fmtPublishDate(detail.previous.publishedAt)}` : 'No earlier list'}
                        disabled={!detail.previous}
                        onClick={() => detail.previous && updateParams({ v: detail.previous.version, ticker: null })}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </StepButton>
                      <StepButton
                        t={t}
                        label={detail.next ? `Next: ${fmtPublishDate(detail.next.publishedAt)}` : 'This is the latest list'}
                        disabled={!detail.next}
                        onClick={() => detail.next && updateParams({ v: detail.next.version, ticker: null })}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </StepButton>
                    </Box>
                  </Box>

                  <ChangesPanel
                    changes={detail.changes}
                    previousDate={detail.previous?.publishedAt ?? null}
                    onTicker={ticker => updateParams({ ticker })}
                  />

                  <FrozenTable entries={detail.entries} onTicker={ticker => updateParams({ ticker })} />
                </>
              )}
            </Box>
          </Box>
        )}
      </Container>

      <TickerTimelinePanel
        ticker={openTicker}
        service={service}
        onClose={() => updateParams({ ticker: null })}
      />
    </Box>
  )
}

function StepButton({ t, label, disabled, onClick, children }: {
  t: C100Tokens
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Tooltip title={label} disableInteractive>
      {/* span so the tooltip still fires on a disabled button */}
      <span>
        <IconButton
          aria-label={label}
          size="small"
          disabled={disabled}
          onClick={onClick}
          sx={{ border: `1px solid ${t.hairline}`, color: t.text, '&.Mui-disabled': { color: t.hairline } }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  )
}

function Notice({ t, onRetry, children }: { t: C100Tokens; onRetry?: () => void; children: ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: t.card,
        border: `1px solid ${t.hairline}`,
        borderRadius: 2,
        p: 4,
        textAlign: 'center'
      }}
    >
      <Typography sx={{ color: t.text, fontFamily: MONO, fontSize: 13 }}>{children}</Typography>
      {onRetry && (
        <Box
          component="button"
          onClick={onRetry}
          sx={{
            mt: 2.5,
            cursor: 'pointer',
            fontFamily: MONO,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: t.accent,
            bgcolor: t.faintAccentBg,
            border: `1px solid ${t.accent}`,
            borderRadius: 999,
            px: 2.5,
            py: 0.75,
            '&:hover': { bgcolor: t.accent, color: '#fff' }
          }}
        >
          Retry
        </Box>
      )}
    </Box>
  )
}
